import React, { useState, useEffect } from 'react';
import ShareButtons from './ShareButtons';
import { getArticleTranslation } from '../services/api';
import { LANGUAGES, RTL_LANGUAGES } from '../constants/languages';

const FALLBACK_IMG = '/static/weeklydefense_theme.png';

const ArticleCard = ({ article, onClick, formatDate, truncateText, onCardClick, language = 'en' }) => {
  const imageUrl = article.banner_image_url
    || (article.images && article.images[0])
    || FALLBACK_IMG;

  const sourceName = article.source_name;

  // ── Per-card language override ──────────────────────────────────────────────
  // `override` holds a fetched translation when this card's language differs
  // from the grid's global language; null means "just show article.* as-is".
  const [cardLang, setCardLang] = useState(language);
  const [override, setOverride] = useState(null);
  const [loadingOverride, setLoadingOverride] = useState(false);

  // Global language switch clears any per-card override so it follows the grid again.
  useEffect(() => {
    setCardLang(language);
    setOverride(null);
  }, [language, article.id]);

  const title = override ? override.title : article.title;
  const summary = override ? override.summary : article.summary;
  const keyTakeaways = override ? override.key_takeaways : article.key_takeaways;
  const isRtl = RTL_LANGUAGES.includes(cardLang);

  const handleLangChange = async (e) => {
    const newLang = e.target.value;
    setCardLang(newLang);

    if (newLang === language) {
      setOverride(null);
      return;
    }

    setLoadingOverride(true);
    try {
      const data = await getArticleTranslation(article.id, newLang);
      setOverride({ title: data.title, summary: data.summary, key_takeaways: data.key_takeaways });
    } catch (err) {
      console.error('Failed to load card translation:', err);
      setCardLang(language);
      setOverride(null);
    } finally {
      setLoadingOverride(false);
    }
  };

  const handleClick = async () => {
    if (onCardClick) {
      await onCardClick(article.id);
    }
    onClick(override ? { ...article, title, summary, key_takeaways: keyTakeaways } : article);
  };

  const handleImageError = (e) => {
    if (e.target.src.endsWith(FALLBACK_IMG)) return;
    e.target.src = FALLBACK_IMG;
  };

  return (
    <div className="article-card" onClick={handleClick}>
      <div className="card-image">
        <img
          src={imageUrl}
          alt={title}
          onError={handleImageError}
        />
        <span className="card-source-badge">{sourceName}</span>
      </div>
      <div className="card-content">
        <div className="card-meta">
          <span className="card-date">{formatDate(article.scraped_at)}</span>
          {article.reading_time && (
            <span className="card-reading-time">{article.reading_time} min read</span>
          )}
        </div>
        <div dir={isRtl ? 'rtl' : 'ltr'} style={loadingOverride ? { opacity: 0.5 } : undefined}>
          <h3 className="card-title">{title}</h3>
          <p className="card-summary">{truncateText(summary, 120)}</p>
        </div>
        <div className="card-footer">
          <span className="read-more">Read Summary →</span>
          {article.click_count > 0 && (
            <span className="click-count">{article.click_count} views</span>
          )}
          <div className="card-language-select" onClick={e => e.stopPropagation()}>
            <select
              value={cardLang}
              onChange={handleLangChange}
              aria-label="Card language"
              title="View this card in another language"
            >
              {LANGUAGES.map(l => (
                <option
                  key={l.code}
                  value={l.code}
                  disabled={l.code !== 'en' && !(article.available_languages || []).includes(l.code)}
                >
                  {l.label}
                </option>
              ))}
            </select>
          </div>
            <div className="card-share" onClick={e => e.stopPropagation()}>
                 <ShareButtons title={article.title} articleId={article.id} compact={true} />
            </div>
        </div>
      </div>
    </div>
  );
};
export default ArticleCard;
